<template>
  <div :class="['global-auth-box d-flex justify-center align-center', {hide:!show}]" @click="close">
    <div class="g-auth-box" @click.stop>
      <div class="back-box">
        <span @click="showGlobalLogin">
          <i class="fas fa-chevron-left fa-fw"></i>
          Login
        </span>
      </div>

      <div class="page-title">Sign Up</div>

      <div v-if="step === 1">
        <div>To begin, please enter your mobile number.</div>
        <mobile-input :needVerification="false" :mobile="mobile" :uniqID="'mobile1'"
          @updateMobile="updateMobileInfo" ref="userMobile" :dialCode="countryCode"
          :required="true" :hasError="error.fields.indexOf('mobile') > -1"></mobile-input>
      </div>

      <div v-if="step === 2">
        <div>
          Please enter the verification code we sent to
          <b>+{{countryCode}}{{mobile}}</b>
        </div>
        <div :class="['pax-field', {'has-error':error.fields.indexOf('pin') > -1}]">
          <input type="text" placeholder="Type in the verification code here." v-model="pin" :disabled="mobileLoading" />
        </div>
      </div>

      <div v-if="step === 3">
        <div class="d-flex justify-space-between" style="gap: 10px">
          <div :class="['pax-field', {'has-error':error.fields.indexOf('gender') > -1}]" @change="gtagTrack('gender')">
            <select v-model="gender" :class="['pax-bday', {'has-error':error.fields.indexOf('gender') > -1}]">
              <option value="">--</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </div>
          <div :class="['pax-field', {'has-error':error.fields.indexOf('title') > -1}]" @change="gtagTrack('title')">
            <select v-model="title" :class="['pax-bday', {'has-error':error.fields.indexOf('title') > -1}]">
              <option value="">--</option>
              <option value="Mr">Mr.</option>
              <option value="Ms">Ms.</option>
              <option value="Mrs">Mrs.</option>
              <option value="Miss">Miss</option>
              <option value="Mstr">Mstr</option>
            </select>
          </div>
        </div>
        <div class="d-flex justify-space-between" style="gap: 10px">
          <div :class="['pax-field', {'has-error':error.fields.indexOf('firstName') > -1}]" @change="gtagTrack('firstName')">
            <input type="text" placeholder="First Name" v-model="firstName" />
          </div>
          <div :class="['pax-field', {'has-error':error.fields.indexOf('lastName') > -1}]" @change="gtagTrack('lastName')">
            <input type="text" placeholder="Last Name" v-model="lastName" />
          </div>
        </div>

        <div class="pax-field d-flex justify-space-between" style="gap: 10px">
          <select v-model="birthMonth" :class="['pax-bday', {'has-error':error.fields.indexOf('birthMonth') > -1}]">
            <option value="" disabled selected>Birth Month</option>
            <option value="01">Jan</option>
            <option value="02">Feb</option>
            <option value="03">Mar</option>
            <option value="04">Apr</option>
            <option value="05">May</option>
            <option value="06">Jun</option>
            <option value="07">Jul</option>
            <option value="08">Aug</option>
            <option value="09">Sep</option>
            <option value="10">Oct</option>
            <option value="11">Nov</option>
            <option value="12">Dec</option>
          </select>
          <select v-model="birthDay" :class="['pax-bday', {'has-error':error.fields.indexOf('birthDay') > -1}]">
            <option value="" disabled selected>Birth Day</option>
            <option v-for="x in 31" :value="x" :key="x">{{x}}</option>
          </select>
          <select v-model="birthYear" :class="['pax-bday', {'has-error':error.fields.indexOf('birthYear') > -1}]">
            <option value="" disabled selected>Birth Year</option>
            <option :value="currentYear">{{currentYear}}</option>
            <option v-for="x in 99" :value="currentYear - x" :key="x">{{currentYear - x}}</option>
          </select>
        </div>

        <div class="d-flex justify-space-between" style="gap: 10px">
          <div :class="['pax-field', {'has-error':error.fields.indexOf('address') > -1}]" @change="gtagTrack('address')">
            <input type="text" placeholder="Address" v-model="address" />
          </div>
          <div :class="['pax-field', {'has-error':error.fields.indexOf('city') > -1}]" @change="gtagTrack('city')">
            <input type="text" placeholder="City" v-model="city" />
          </div>
        </div>

        <div class="d-flex justify-space-between" style="gap: 10px">
          <div :class="['pax-field', {'has-error':error.fields.indexOf('zip') > -1}]" @change="gtagTrack('zip')">
            <input type="text" placeholder="Zip" v-model="zip" />
          </div>
          <div class="pax-field">
            <select :class="['country-selector', {'has-error':error.fields.indexOf('country') > -1}]" v-model="country" placeholder="Country">
              <option value="" disabled>Select Country</option>
              <option v-for="(country, i) in countryList" :key="i" :value="country.code">{{cleanCountryName(country.name)}}</option>
            </select>
          </div>
        </div>

        <div class="pax-field">
          <input type="text" placeholder="Passport Number (Optional)" v-model="passportNum" autocomplete="one-time-code" />
        </div>

        <div :class="['pax-field', {'has-error':error.fields.indexOf('email') > -1}]">
          <input type="email" placeholder="Email" v-model="email" autocomplete="one-time-code"  @change="gtagTrack('email')" />
        </div>
        <div class="d-flex" style="gap: 10px">
          <div :class="['pax-field', {'has-error':error.fields.indexOf('password') > -1}]">
            <input type="password" placeholder="Password" v-model="password"
              autocomplete="one-time-code" />
          </div>
          <div :class="['pax-field', {'has-error':error.fields.indexOf('rpassword') > -1}]">
            <input type="password" placeholder="Repeat Password" v-model="rpassword"
              autocomplete="one-time-code" />
          </div>
        </div>
      </div>

      <div class="error-msg" v-if="error.msg.length">
        <div v-if="error.msg.indexOf('E_EMAIL_EXISTS') > -1">
          {{ $t('customerForm.emailExist') }}
          <a href="javascript:;" v-on:click="initShowLogin">{{ $t('customerForm.clickHere') }}</a> 
          {{ $t('customerForm.toSignIn') }}
          <br/>
          {{ $t('customerForm.otherwisePlease') }}
        </div>
        <div v-else v-for="(err, e) in error.msg" :key="e">- {{err}}</div>
      </div>

      <div :class="['op-btn btn-md btn-block btn-success signup-btn', {disabled:mobileLoading}]" @click="submit">
        <i class="fas fa-spinner fa-spin slow-spin fa-fw" v-if="mobileLoading"></i>
        <span v-if="step === 1">
          <span v-if="(countryCode === firstVisit.countryCode && mobile === firstVisit.mobile && firstVisit.mobile) || global.userInfo.verifiedNumber.indexOf(`${countryCode}${mobile}`) > -1">NEXT</span>
          <span v-else>SEND VERIFICATION CODE</span>
        </span>
        <span v-else-if="step === 2">VERIFY</span>
        <span v-else>SIGN UP</span>
      </div>
      <div class="text-center" v-if="step === 2">
        <span :class="['resend-txt', {disabled:resendTimer > 0}]" @click="resendPin">
          Resend Verification Code
          <span v-if="resendTimer > 0">({{resendTimer}}s)</span>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment-timezone'
import mobileInput from '@/components/D2DOthers/MobileInput'
import WidgetSession from '@/modules/session'
const countries = require('@/data/countries')

export default {
  name: 'auth-signup',
  components: { mobileInput },
  data () {
    return {
      title: '',
      gender: '',
      firstName: '',
      lastName: '',
      mobile: '',
      countryCode: 1,
      isVerified: false,
      birthDay: '',
      birthMonth: '',
      birthYear: '',
      address: '',
      city: '',
      zip: '',
      country: 'us',
      countryList: Object.values(countries),
      passportNum: '',
      email: '',
      password: '',
      rpassword: '',
      error: {
        fields: [],
        msg: []
      },
      pin: '',
      mobileLoading: false,
      resendTimer: 0,
      resendDelay: '',
      session: '',
      currentYear: parseInt(moment().format('YYYY'))
    }
  },
  computed: {
    user () { return this.$store.state.user },
    auth () { return this.$store.state.global.auth },
    show: {
      get () { return this.$store.state.global.auth.showSignup },
      set (x) {
        this.step = 1
        if (this.auth.action === 'forceAuth') return
        this.$store.commit('global/setAuthData', {showSignup: x})
      }
    },
    step: {
      get () { return this.$store.state.global.auth.signup.step },
      set (x) { this.$store.commit('global/setAuthSignupData', {step: x}) }
    },
    firstVisit () { return this.user.firstVisit },
    global () { return this.$store.state.global }
  },
  methods: {
    showGlobalLogin () {
      this.$store.commit('global/setAuthData', {
        show: true,
        signupSelect: false,
        showSignup: false
      })
    },
    close () {
      this.show = false
      this.error.fields = []
      this.error.msg = []
    },
    initShowSignup () {
      this.login.email = ''
      this.login.password = ''
      this.login.error.fields = []
      this.login.error.msg = []
      this.showSignup = true
    },
    submit () {
      if (this.step === 1) {
        if (
          (this.countryCode === this.firstVisit.countryCode && this.mobile && this.mobile === this.firstVisit.mobile) ||
          (this.global.userInfo.verifiedNumber.indexOf(`${this.countryCode}${this.mobile}`) > -1)
        ) {
          this.isVerified = true
          this.step = 3
          return
        }
        this.sendVerificationCode()
      } else if (this.step === 2) this.verifyCode()
      else this.signupSubmit()
    },
    sendVerificationCode () {
      let self = this

      self.error.fields = []
      self.error.msg = []
      if (!self.mobile) {
        self.error.fields.push('mobile')
        self.error.msg.push('Please enter your mobile number.')
        return
      }

      let expected = (`${self.mobile}${self.countryCode}` >> 2) * 5
      self.mobileLoading = true
      self.isVerified = false
      self.$sdk.post(self, '/auth/sms/code', {
        phone: self.mobile,
        countryCode: `+${self.countryCode}`,
        code: expected
      }, response => {
        self.mobileLoading = false
        if (!response.notes) {
          self.resendTimer = 180
          self.step = 2
          self.startCountdown()
        } else {
          self.error.msg.push(self.$t('global.oops'))
        }
      }, '/1.1')
    },
    startCountdown () {
      let self = this
      if (self.resendDelay) clearTimeout(self.resendDelay)

      self.resendDelay = setTimeout(function () {
        self.resendTimer--
        if (self.resendTimer === 0) return
        self.startCountdown()
      }, 1000)
    },
    verifyCode () {
      let self = this

      self.error.fields = []
      self.error.msg = []
      if (!self.pin) {
        self.error.fields.push('pin')
        self.error.msg.push('Please enter verification code.')
        return
      }

      self.mobileLoading = true
      self.$sdk.post(self, '/auth/sms/verify', {
        phone: self.mobile,
        countryCode: self.countryCode,
        smsCode: self.pin
      }, res => {
        self.mobileLoading = false
        if (res.error) {
          self.error.msg.push(self.$t('global.invalidPin'))
          return
        }
        self.isVerified = true
        self.step = 3
        this.$store.commit('global/addVerifiedNumber', `${self.countryCode}${self.mobile}`)
      })
    },
    resendPin () {
      if (this.resendTimer !== 0) return
      this.sendVerificationCode()
    },
    mobileVerified (val) {
      this.isVerified = val
    },
    updateMobileInfo (val) {
      this.mobile = val.mobile
      this.countryCode = val.countryCode

      // if (window.gtag) { // eslint-disable-line
      //   gtag( // eslint-disable-line
      //     'event',
      //     'Signup_Phone number',
      //     {value: `${val.countryCode}${val.mobile}`}
      //   )
      // }
    },
    initShowLogin () {
      this.email = ''
      this.password = ''
      this.firstName = ''
      this.lastName = ''
      this.mobile = ''
      this.countryCode = ''
      this.rpassword = ''
      this.error.fields = []
      this.error.msg = []
      this.showSignup = false
    },
    signupSubmit () {
      let self = this
      let hasError = false
      let fields = [
        'title', 'gender',
        'firstName', 'lastName', 'email',
        'birthMonth', 'birthDay', 'birthYear',
        'address', 'city', 'zip',
        'password', 'rpassword'
      ]
      self.error.fields = []
      self.error.msg = []
      fields.forEach(f => {
        if (!self[f]) {
          hasError = true
          self.error.fields.push(f)
        }
      })
      if (self.error.fields.length) {
        self.error.msg.push('Please fill up the missing fields.')
      }
      if (self.mobile && !self.isVerified) {
        hasError = true
        self.error.msg.push('Phone verification is required.')
      }
      let pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      if (self.email && !pattern.test(self.email)) {
        hasError = true
        self.error.msg.push(self.$t('common.invalidEmail'))
        self.error.fields.push('email')
      }
      let bday = ''
      if (self.birthDay && self.birthMonth && self.birthYear) {
        bday = moment(`${self.birthYear}-${self.birthMonth}-${self.birthDay}`, 'YYYY-MM-DD')
        if (!bday.isValid()) {
          bday = ''
          hasError = true
          self.error.msg.push('Invalid Birth Date')
          self.error.fields.push('birthMonth', 'birthDay', 'birthYear')
        }
      }
      if (self.password && self.password.length < 8) {
        hasError = true
        self.error.msg.push('Password must be at least 8 characters.')
      }
      if (self.rpassword && self.rpassword.length < 8) {
        hasError = true
        self.error.msg.push('Repeat password must be at least 8 characters.')
      }
      if (self.password && self.rpassword && self.password !== self.rpassword) {
        hasError = true
        self.error.msg.push(self.$t('customerForm.passwordNotMatch'))
      }
      if (hasError) return

      let newUserToken = this.session.generateUserToken(false)
      self.$store.commit('global/setLoaderData', {show: true})
      self.$sdk.post(self, '/auth/signup', {
        email: self.email,
        password: self.password,
        device: newUserToken,
        paxinfo: {
          title: self.title,
          gender: self.gender,
          address: self.address,
          city: self.city,
          zip: self.zip
        },
        firstName: self.firstName,
        lastName: self.lastName,
        phone: self.countryCode + self.mobile,
        dateOfBirth: bday.format('YYYY-MM-DD'),
        userCountry: self.country.toUpperCase(),
        passportNum: self.passportNum,
        wheelchair: false,
        serviceDog: false,
        brandID: self.$store.state.brandId,
        d2d: true
      }, res => {
        if (res.error) {
          self.signupFailure(res)
          self.$store.commit('global/setLoaderData', {show: false})
          return
        }
        res.token = newUserToken
        self.session.useToken(newUserToken) // Only save the token if successful
        // self.saveAddress(res)
        self.signupSuccess(res)
      }, '1.1')
    },
    saveAddress (data) {
      let self = this
      self.$sdk.get(self, '/me/locations', {token: data.token}, res => {
        self.signupSuccess(data)
        if (res.error) return
        let home = res.home
        let tmp = {
          address: home && home.addressLine1 ? home.addressLine1 : '',
          city: home && home.city ? home.city : '',
          zip: home && home.zipCode ? home.zipCode : ''
        }
        self.$store.commit('user/setUserData', tmp)
      }, '1.1')
    },
    signupSuccess (data) {
      let self = this
      self.$store.commit('global/setLoaderData', {show: false})
      data.phone = self.mobile
      data.countryCode = self.countryCode
      data.bday = moment(self.bday).format('YYYY-MM-DD')
      data.country = self.country.toUpperCase()
      if (data.paxinfo) {
        data.title = data.paxinfo.title
        data.gender = data.paxinfo.gender
        self.$store.commit('user/setUserData', {
          address: data.paxinfo.address || '',
          city: data.paxinfo.city || '',
          zip: data.paxinfo.zip || ''
        })
      }
      self.$store.commit('user/setUserInfo', data)
      self.$store.commit('user/setToken', data.token)
      self.$store.commit('global/setAuthData', {show: false})
      self.show = false
      if (self.auth.action && self.auth.action !== 'forceAuth') {
        self.$router.push(self.auth.action)
        self.$store.commit('global/setAuthData', {action: ''})
      }
    },
    signupFailure (data) {
      this.$store.commit('global/setLoaderData', {show: false})
      let msg = data.error.message.replace('backend.', '')
      this.$store.commit('global/setAlertData', {
        show: true,
        message: msg
      })
    },
    cleanCountryName (val) {
      let val2 = val.slice(0, val.indexOf(" ("))
      return val.indexOf(" (") === -1 ? val : val2
    },
    gtagTrack (field) {
      // if (window.gtag) { // eslint-disable-line
      //   gtag( // eslint-disable-line
      //     'event',
      //     `Signup_${field}`,
      //     {value: this[field]}
      //   )
      // }
    }
  },
  watch: {
    show (x) {
      console.log('signup show', x)
      if (x) {
        this.step = this.auth.signup.step || 1
        this.mobile = this.auth.signup.mobile || ''
        if (this.mobile) this.isVerified = true
        this.countryCode = this.auth.signup.countryCode || 1
        this.email = this.auth.signup.email || ''
      }
    }
  },
  mounted () {
    this.session = new WidgetSession(this)

    let firstVisit = localStorage.getItem('firstVisitData')
    if (firstVisit) {
      firstVisit = JSON.parse(firstVisit)
      if (firstVisit.mobile) this.mobile = firstVisit.mobile
      if (firstVisit.countryCode) this.countryCode = firstVisit.countryCode
    }
  }
}
</script>

<style lang="scss" scoped>
.global-auth-box{
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0,0,0, .3);
  z-index: 2;

  .g-auth-box{
    background: #f9f9f9;
    border-radius: 4px;
    box-shadow: 2px 2px 4px rgba(0,0,0, .3);
    padding: 20px;
    width: 100%;
    max-width: 500px;
    -webkit-transition: all 0.1s ease;
    -moz-transition: all 0.1s ease;
    -o-transition: all 0.1s ease;
    transition: all 0.1s ease;
  }
  &.hide {
    z-index: -1;
    opacity: 0;
    display: none !important;

    .g-auth-box{
      transform: scale(90%) translateY(30px);
    }
  }
}
.back-box{
  color: #708193;

  i{
    color: #a7b9cb;
    vertical-align: -1px;
  }
  span{
    cursor: pointer;
    &:hover *{
      color: #2c3e50;
    }
  }
}
.page-title{
  font-size: 21px;
  font-weight: 600;
  padding: 15px 0;
  text-align: center;
}
.resend-txt{
  text-decoration: underline;
  cursor: pointer;
  margin-top: 5px;
  display: inline-block;
  transition: all .3s ease;

  &.disabled{
    color: #aaa;
    cursor: default;
    text-decoration: none;
  }
}
select{
  border-color: #ccc;
  border-radius: 0;
  line-height: 1;
  box-shadow: inset 1px 1px 2px rgba(0,0,0,.1);
}
.pax-bday{
  padding: 10px;
  &.has-error{
    border:1px solid #EB5837;
  }
}
.pax-field{
  margin-bottom: 10px;
  width: 100%;

  input{
    padding: 10px;
    line-height: 1;
    box-shadow: inset 1px 1px 2px rgba(0,0,0,.1);
    border: 1px solid #ccc;
    width: 100%;
  }
  &.has-error{
    border:1px solid #EB5837;
  }
}
.country-selector{
  padding: 10px;
}
.error-msg{
  border: 1px solid #f00;
  background: #fadfdf;
  color: #f00;
  padding: 5px;
  line-height: 1.3;
  border-radius: 2px;
  margin: 10px 0 20px;
  text-align: left;

  a{
    color: #285cc7;
    font-weight: 600;
  }
}
.signup-btn{
  margin: 20px 0 5px;
}

</style>



// WEBPACK FOOTER //
// GlobalSignup.vue?c45e008c